import { useEffect, useState } from "react";
import { type LeadSide, kijijiRegions, leadSourcesForSide } from "@hunts/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "../api";
import { ErrorNote } from "./Page";

interface SourceCounts {
  found: number;
  created: number;
  duplicates: number;
  errors: number;
  failure?: string;
}

export interface ScrapeRun {
  id: string;
  status: "RUNNING" | "DONE" | "FAILED";
  regions: string[];
  sources?: string[];
  found: number;
  created: number;
  duplicates: number;
  errors: number;
  bySource?: Record<string, SourceCounts>;
  failure?: string;
  startedAt: string;
}

/**
 * Searching for leads, over the list it will add to.
 *
 * Offered as a sheet rather than its own screen because it is a thing you do to
 * a list, not a place you go -- and only the sources for this side are shown,
 * since a search started from the cleaner leads has no business returning
 * office buildings.
 *
 * The run outlives the sheet. It takes minutes, so closing this does not stop
 * it; the record keeps the result and the list refreshes when it lands.
 */
export function FindLeadsSheet({ side, onClose }: { side: LeadSide; onClose: () => void }) {
  const qc = useQueryClient();
  const sourcesForSide = leadSourcesForSide(side);

  const [sources, setSources] = useState<string[]>(sourcesForSide.map((s) => s.slug));
  const [regions, setRegions] = useState<string[]>(kijijiRegions.map((r) => r.slug));
  const [pages, setPages] = useState(3);
  const [runId, setRunId] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const active = useQuery({
    queryKey: ["scrapes", runId],
    queryFn: () => api.get<ScrapeRun>("scrapes", runId!),
    enabled: runId !== null,
    refetchInterval: (q) => (q.state.data?.status === "RUNNING" ? 3000 : false),
  });

  const start = useMutation({
    mutationFn: () =>
      api.create<ScrapeRun>("scrapes", { regions, sources, maxPagesPerRegion: pages }),
    onSuccess: (run) => setRunId(run.id),
  });

  const run = active.data;
  const running = run?.status === "RUNNING";
  const settled = run && !running ? run.status : null;

  // Invalidating during render feeds back into the query cache mid-render and
  // loops, so the refresh happens here instead.
  useEffect(() => {
    if (!settled) return;
    for (const key of ["leads", "summary", "scrapes"]) {
      void qc.invalidateQueries({ queryKey: [key] });
    }
  }, [settled, runId, qc]);

  const needsRegions = sources.some(
    (slug) => sourcesForSide.find((s) => s.slug === slug)?.usesRegions,
  );

  const toggle = (list: string[], value: string) =>
    list.includes(value) ? list.filter((v) => v !== value) : [...list, value];

  return (
    <div className="sheet" role="dialog" aria-modal="true" aria-label="Find more leads">
      <button className="sheet__scrim" aria-label="Close" onClick={onClose} />
      <div className="sheet__panel">
        <header className="sheet__head">
          <h2>Find more leads</h2>
          <button className="sheet__close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </header>

        <fieldset className="field" style={{ border: 0, padding: 0, margin: 0 }}>
          <legend className="field__label">Where to look</legend>
          {sourcesForSide.map((s) => (
            <label key={s.slug} className="check">
              <input
                type="checkbox"
                checked={sources.includes(s.slug)}
                disabled={running}
                onChange={() => setSources((prev) => toggle(prev, s.slug))}
              />
              <span>
                <strong>{s.label}</strong>
                <span className="muted"> — {s.hint}</span>
              </span>
            </label>
          ))}
        </fieldset>

        {needsRegions && (
          <>
            <fieldset className="field" style={{ border: 0, padding: 0, margin: 0 }}>
              <legend className="field__label">Regions</legend>
              {kijijiRegions.map((region) => (
                <label key={region.slug} className="check">
                  <input
                    type="checkbox"
                    checked={regions.includes(region.slug)}
                    disabled={running}
                    onChange={() => setRegions((prev) => toggle(prev, region.slug))}
                  />
                  <span>
                    <strong>{region.label}</strong>
                    <span className="muted"> — {region.towns.join(", ")}</span>
                  </span>
                </label>
              ))}
              <span className="field__help">
                Kijiji groups the smaller corridor towns into these three, so all
                eleven service-area towns are covered.
              </span>
            </fieldset>

            <label className="field">
              <span className="field__label">Pages per region</span>
              <input
                type="number"
                min={1}
                max={5}
                value={pages}
                disabled={running}
                onChange={(e) =>
                  setPages(Math.min(5, Math.max(1, Number(e.target.value) || 1)))
                }
              />
              <span className="field__help">
                Kijiji only, and extra pages are fetched only when there are that
                many results.
              </span>
            </label>
          </>
        )}

        {start.error != null && <ErrorNote error={start.error} />}

        {run && (
          <div className={`note ${run.status === "FAILED" ? "note--error" : ""}`}>
            {running ? (
              <p>
                <strong>Searching…</strong> This takes a minute or two — some sources
                wait a few seconds between requests on purpose. You can close this;
                the run keeps going.
              </p>
            ) : run.status === "FAILED" ? (
              <p>
                <strong>The search failed.</strong> {run.failure ?? "No detail recorded."}
              </p>
            ) : (
              <>
                <p>
                  <strong>Done.</strong> Found {run.found} — <strong>{run.created} new</strong>,{" "}
                  {run.duplicates} already on file
                  {run.errors > 0 ? `, ${run.errors} could not be saved` : ""}.
                </p>
                {run.bySource && Object.keys(run.bySource).length > 1 && (
                  <ul className="muted" style={{ fontSize: "0.88rem", margin: "0.4rem 0 0 1rem" }}>
                    {Object.entries(run.bySource).map(([name, c]) => (
                      <li key={name}>
                        {name}: {c.created} new of {c.found}
                        {c.failure ? ` — ${c.failure}` : ""}
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </div>
        )}

        <div className="sheet__actions">
          <button
            className="btn btn--primary"
            disabled={sources.length === 0 || start.isPending || running}
            onClick={() => start.mutate()}
          >
            {start.isPending ? "Starting…" : running ? "Running…" : "Search"}
          </button>
          <button className="btn btn--ghost" onClick={onClose}>
            {settled ? "Close" : "Cancel"}
          </button>
        </div>
      </div>
    </div>
  );
}
